import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { NavLink, useLocation } from 'react-router';
import {
  ArrowLeftRight as ArrowLeftRightIcon,
  BadgeCheck as BadgeCheckIcon,
  Bell as BellIcon,
  BookUser as BookUserIcon,
  ChartLine as ChartLineIcon,
  ChevronRight as ChevronRightIcon,
  CircleDollarSign as CircleDollarSignIcon,
  CircleHelp as CircleHelpIcon,
  ClipboardList as ClipboardListIcon,
  FileText as FileTextIcon,
  Gauge as GaugeIcon,
  IdCard as IdCardIcon,
  Key as KeyIcon,
  LogIn as LogInIcon,
  Mail as MailIcon,
  Package as PackageIcon,
  Plug as PlugIcon,
  Repeat as RepeatIcon,
  Send as SendIcon,
  Settings as SettingsIcon,
  ShieldCheck as ShieldCheckIcon,
  TrendingUp as TrendingUpIcon,
  TriangleAlert as TriangleAlertIcon,
  User as UserIcon,
  UserRoundPlus as UserRoundPlusIcon,
  Users as UsersIcon,
} from 'lucide-react';
import { useAppSelector } from '@/store/hooks';
import { hasRole, type OrgRole } from '@/types/organization';
import { usePermissions } from '@/hooks/usePermissions';

interface NavItem {
  name: string;
  /** i18n key under the `nav` group; falls back to `name` if absent. */
  labelKey?: ParseKeys;
  path: string;
  icon: React.ReactNode;
  requiresOrg?: boolean;
  minRole?: OrgRole;
  requireInvitePermission?: boolean;
  badge?: 'pendingInvites' | 'pendingSetups' | 'signingRequests' | 'notifications';
}

const navItems: NavItem[] = [
  {
    name: 'Dashboard',
    labelKey: 'nav.dashboard',
    path: '/dashboard',
    icon: <GaugeIcon />,
    requiresOrg: true,
  },
  {
    name: 'Organizations',
    labelKey: 'nav.organizations',
    path: '/organizations',
    icon: <UsersIcon />,
  },
  {
    name: 'Members',
    labelKey: 'nav.members',
    path: '/members',
    icon: <UserIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Invitations',
    labelKey: 'nav.invitations',
    path: '/invitations',
    icon: <MailIcon />,
    requiresOrg: true,
    requireInvitePermission: true,
  },
  {
    name: 'Policies',
    labelKey: 'nav.policies',
    path: '/org-policies',
    icon: <ClipboardListIcon />,
    requiresOrg: true,
    minRole: 'admin',
  },
  {
    name: 'Approval Groups',
    labelKey: 'nav.approvalGroups',
    path: '/approval-groups',
    icon: <UserRoundPlusIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Pending Changes',
    labelKey: 'nav.pendingChanges',
    path: '/policy-changes',
    icon: <BadgeCheckIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Settings',
    labelKey: 'nav.settings',
    path: '/settings',
    icon: <SettingsIcon />,
    requiresOrg: true,
    minRole: 'admin',
  },
  {
    name: 'Integrations',
    labelKey: 'nav.integrations',
    path: '/integrations',
    icon: <PlugIcon />,
    requiresOrg: true,
    minRole: 'admin',
  },
  {
    name: 'Billing',
    labelKey: 'nav.billing',
    path: '/billing',
    icon: <FileTextIcon />,
    requiresOrg: true,
    minRole: 'owner',
  },
  {
    name: 'Pricing',
    labelKey: 'nav.pricing',
    path: '/pricing',
    icon: <CircleDollarSignIcon />,
  },
];

const vaultNavItems: NavItem[] = [
  {
    name: 'Portfolio',
    labelKey: 'nav.portfolio',
    path: '/portfolio',
    icon: <TrendingUpIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Vaults',
    labelKey: 'nav.vaults',
    path: '/vaults',
    icon: <ShieldCheckIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Contacts',
    labelKey: 'nav.contacts',
    path: '/contacts',
    icon: <BookUserIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Analytics',
    labelKey: 'nav.analytics',
    path: '/analytics',
    icon: <ChartLineIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Spam & Threats',
    labelKey: 'nav.spamThreats',
    path: '/spam-threats',
    icon: <TriangleAlertIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Transfers',
    labelKey: 'nav.transfers',
    path: '/transfers',
    icon: <ArrowLeftRightIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Recurring Payments',
    labelKey: 'nav.recurringPayments',
    path: '/recurring-payments',
    icon: <RepeatIcon />,
    requiresOrg: true,
    minRole: 'viewer',
  },
  {
    name: 'Signing Requests',
    labelKey: 'nav.signingRequests',
    path: '/signing-requests',
    icon: <SendIcon />,
    requiresOrg: true,
    badge: 'signingRequests',
  },
  {
    name: 'Setup Requests',
    labelKey: 'nav.setupRequests',
    path: '/vault-setup',
    icon: <KeyIcon />,
    requiresOrg: true,
    badge: 'pendingSetups',
  },
];

const activityNavItems: NavItem[] = [
  {
    name: 'Overview',
    labelKey: 'nav.overview',
    path: '/activity',
    icon: <TrendingUpIcon />,
    requiresOrg: true,
    minRole: 'admin',
  },
  {
    name: 'Audit Log',
    labelKey: 'nav.auditLog',
    path: '/activity/audit-log',
    icon: <FileTextIcon />,
    requiresOrg: true,
    minRole: 'admin',
  },
  {
    name: 'Vault Activity',
    labelKey: 'nav.vaultActivity',
    path: '/activity/vault-activity',
    icon: <PackageIcon />,
    requiresOrg: true,
    minRole: 'admin',
  },
  {
    name: 'Critical Actions',
    labelKey: 'nav.criticalActions',
    path: '/activity/critical-actions',
    icon: <ShieldCheckIcon />,
    requiresOrg: true,
    minRole: 'admin',
  },
  {
    name: 'Login Activity',
    labelKey: 'nav.loginActivity',
    path: '/activity/login-activity',
    icon: <LogInIcon />,
    requiresOrg: true,
    minRole: 'admin',
  },
];

const bottomNavItems: NavItem[] = [
  {
    name: 'Notifications',
    labelKey: 'nav.notifications',
    path: '/notifications',
    icon: <BellIcon />,
    badge: 'notifications',
  },
  {
    name: 'Profile',
    labelKey: 'nav.profile',
    path: '/profile',
    icon: <IdCardIcon />,
  },
];

const DOCS_URL = 'https://docs.sspwallet.io';

interface SidebarProps {
  isVisible: boolean;
  isMobileOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isVisible, isMobileOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const currentOrgId = useAppSelector((state) => state.organization.currentOrgId);
  const currentOrg = useAppSelector((state) =>
    state.organization.organizations.find((o) => o.id === currentOrgId)
  );
  const myInvitations = useAppSelector((state) => state.organization.myInvitations);
  const pendingSetups = useAppSelector((state) => state.vault.pendingSetups);
  const signingRequestCount = useAppSelector((state) => state.vault.signingRequestCount);
  const pendingInviteCount = myInvitations.length;
  const pendingSetupCount = pendingSetups.length;

  const currentRole = currentOrg?.membership.role;
  const { canInviteMembers } = usePermissions();

  const [activityExpanded, setActivityExpanded] = useState(
    location.pathname.startsWith('/activity')
  );

  const isItemVisible = (item: NavItem): boolean => {
    if (item.requiresOrg && !currentOrgId) return false;
    if (item.requireInvitePermission && !canInviteMembers) return false;
    if (item.minRole && currentRole && !hasRole(currentRole, item.minRole)) return false;
    return true;
  };

  const getBadgeCount = (badge: NavItem['badge']): number | null => {
    if (badge === 'pendingInvites' && pendingInviteCount > 0) {
      return pendingInviteCount;
    }
    if (badge === 'pendingSetups' && pendingSetupCount > 0) {
      return pendingSetupCount;
    }
    if (badge === 'signingRequests' && signingRequestCount > 0) {
      return signingRequestCount;
    }
    if (badge === 'notifications') {
      const total = pendingInviteCount + pendingSetupCount + signingRequestCount;
      return total > 0 ? total : null;
    }
    return null;
  };

  const renderNavItem = (item: NavItem) => {
    if (!isItemVisible(item)) return null;

    const badgeCount = getBadgeCount(item.badge);

    // For paths with query params, check active state by pathname + search
    const hasQueryParams = item.path.includes('?');
    const isManuallyActive = hasQueryParams
      ? location.pathname + location.search === item.path
      : undefined;

    return (
      <NavLink
        key={item.path}
        to={item.path}
        end={item.path === '/activity' || hasQueryParams}
        className={
          hasQueryParams
            ? `nav-item ${isManuallyActive ? 'active' : ''}`
            : ({ isActive }) => `nav-item ${isActive ? 'active' : ''}`
        }
        onClick={onClose}
      >
        <span className="nav-item-icon">{item.icon}</span>
        <span className="nav-item-label">{item.labelKey ? t(item.labelKey) : item.name}</span>
        {badgeCount !== null && (
          <span
            className={`nav-item-badge ${item.badge === 'pendingInvites' ? 'invitation-badge' : ''}`}
          >
            {badgeCount}
          </span>
        )}
      </NavLink>
    );
  };

  const showActivity = currentOrgId && currentRole && hasRole(currentRole, 'admin');

  return (
    <>
      {/* Overlay only shows on mobile when sidebar is open */}
      <div className={`sidebar-overlay ${isMobileOpen ? 'visible' : ''}`} onClick={onClose} />
      <aside className={`app-sidebar ${isVisible ? 'visible' : ''}`}>
        <nav className="sidebar-content">
          <div className="nav-section">
            <div className="nav-section-title">{t('nav.sectionMain')}</div>
            {navItems.slice(0, 2).map(renderNavItem)}
          </div>

          {currentOrgId && (
            <div className="nav-section">
              <div className="nav-section-title">{t('nav.sectionPortfolio')}</div>
              {vaultNavItems.map(renderNavItem)}
            </div>
          )}

          {currentOrgId && (
            <div className="nav-section">
              <div className="nav-section-title">{t('nav.sectionOrganization')}</div>
              {navItems.slice(2).map(renderNavItem)}
            </div>
          )}

          {showActivity && (
            <div className="nav-section" style={{ marginTop: 'auto' }}>
              <button
                className="nav-section-toggle"
                onClick={() => {
                  setActivityExpanded((prev) => !prev);
                }}
              >
                <span className="nav-section-toggle-icon-wrap">
                  <ChartLineIcon />
                </span>
                <span className="nav-section-toggle-label">{t('nav.sectionActivity')}</span>
                <ChevronRightIcon
                  className={`nav-section-toggle-chevron ${activityExpanded ? 'expanded' : ''}`}
                />
              </button>
              <div className={`nav-section-children ${activityExpanded ? 'expanded' : ''}`}>
                {activityNavItems.map(renderNavItem)}
              </div>
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="nav-section-title">{t('nav.sectionAccount')}</div>
          {bottomNavItems.map(renderNavItem)}
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-item"
            onClick={onClose}
          >
            <span className="nav-item-icon">
              <CircleHelpIcon />
            </span>
            <span className="nav-item-label">{t('nav.helpAndDocs')}</span>
          </a>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
